<template>
  <Teleport to="body">
    <div v-if="tour.active && step" class="tour" role="dialog" aria-modal="true" aria-label="Guía de Parkos">
      <!-- Captura los clics para que no se navegue por accidente durante la guía. -->
      <div class="tour__shield" @click.stop></div>
      <div v-if="rect" class="tour__spot" :style="spotStyle"></div>
      <div v-else class="tour__dim"></div>

      <div
        ref="cardEl"
        class="tour__card"
        :class="{ 'tour__card--center': placement === 'center', 'tour__card--bottom': placement === 'bottom', 'tour__card--side': placement === 'side' }"
        :style="cardStyle"
      >
        <span v-if="placement === 'side'" class="tour__arrow" :style="{ top: `${arrowTop}px` }"></span>

        <!-- Bienvenida -->
        <template v-if="step.kind === 'welcome'">
          <div class="tour__icon tour__icon--green"><span class="mdi mdi-compass-outline"></span></div>
          <h2 class="tour__title">Te damos la bienvenida{{ firstName ? `, ${firstName}` : '' }}</h2>
          <p class="tour__text">
            Un recorrido de un minuto por lo que puedes hacer en Parkos. Puedes saltártelo cuando quieras y volver a verlo después desde el botón «Guía de Parkos» del menú.
          </p>
          <div class="tour__actions">
            <button type="button" class="tour__btn tour__btn--ghost" @click="finishTour">Ahora no</button>
            <button ref="primaryEl" type="button" class="tour__btn" @click="nextStep">Empezar recorrido</button>
          </div>
        </template>

        <!-- Cierre -->
        <template v-else-if="step.kind === 'end'">
          <div class="tour__icon tour__icon--green"><span class="mdi mdi-check-bold"></span></div>
          <h2 class="tour__title">¡Todo listo!</h2>
          <p class="tour__text">
            Cuando quieras repasar algo, abre de nuevo esta guía con el botón «Guía de Parkos» del menú lateral.
          </p>
          <div class="tour__actions">
            <button v-if="tour.index > 1" type="button" class="tour__btn tour__btn--ghost" @click="prevStep">Anterior</button>
            <button ref="primaryEl" type="button" class="tour__btn" @click="finishTour">Empezar a usar Parkos</button>
          </div>
        </template>

        <!-- Sección del menú -->
        <template v-else>
          <div class="tour__meta">
            <span class="tour__count">{{ sectionNumber }} de {{ sectionTotal }}</span>
            <button type="button" class="tour__skip" @click="finishTour">Omitir guía</button>
          </div>
          <div class="tour__heading">
            <span class="tour__icon"><span class="mdi" :class="step.icon"></span></span>
            <h2 class="tour__title">{{ step.title }}</h2>
          </div>
          <p class="tour__text">{{ step.text }}</p>
          <div class="tour__footer">
            <ol class="tour__dots" aria-hidden="true">
              <li v-for="n in sectionTotal" :key="n" class="tour__dot" :class="{ 'tour__dot--on': n === sectionNumber, 'tour__dot--done': n < sectionNumber }"></li>
            </ol>
            <div class="tour__actions tour__actions--tight">
              <button type="button" class="tour__btn tour__btn--ghost" @click="prevStep">Anterior</button>
              <button ref="primaryEl" type="button" class="tour__btn" @click="nextStep">Siguiente</button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useAuthStore } from '@/services/authStore'
import { tour, startTour, nextStep, prevStep, finishTour } from '@/services/tour'

const { state } = useAuthStore()

const step = computed(() => tour.steps[tour.index] || null)
const sectionSteps = computed(() => tour.steps.filter((s) => s.kind === 'section'))
const sectionTotal = computed(() => sectionSteps.value.length)
const sectionNumber = computed(() => sectionSteps.value.indexOf(step.value) + 1)
const firstName = computed(() => (state.user?.name || '').trim().split(/\s+/)[0] || '')

const rect = ref(null)
const cardEl = ref(null)
const primaryEl = ref(null)
const pos = ref({ left: 0, top: 0 })
const arrowTop = ref(24)
const viewport = ref({ w: window.innerWidth, h: window.innerHeight })

const GAP = 18
const MARGIN = 12
const SPOT_PAD = 4

const compact = computed(() => viewport.value.w < 700)
const placement = computed(() => {
  if (!rect.value) return 'center'
  return compact.value ? 'bottom' : 'side'
})

const spotStyle = computed(() => ({
  left: `${rect.value.left - SPOT_PAD}px`,
  top: `${rect.value.top - SPOT_PAD}px`,
  width: `${rect.value.width + SPOT_PAD * 2}px`,
  height: `${rect.value.height + SPOT_PAD * 2}px`,
}))

const cardStyle = computed(() => (placement.value === 'side' ? { left: `${pos.value.left}px`, top: `${pos.value.top}px` } : {}))

function measureTarget() {
  const target = step.value?.target ? document.querySelector(step.value.target) : null
  if (!target) {
    rect.value = null
    return
  }
  const r = target.getBoundingClientRect()
  rect.value = r.width > 0 && r.height > 0 ? { left: r.left, top: r.top, width: r.width, height: r.height } : null
}

async function place() {
  viewport.value = { w: window.innerWidth, h: window.innerHeight }
  measureTarget()
  await nextTick()
  if (!rect.value || placement.value !== 'side') return
  const cardH = cardEl.value?.offsetHeight || 190
  const cardW = cardEl.value?.offsetWidth || 340
  const center = rect.value.top + rect.value.height / 2
  const top = Math.min(Math.max(center - cardH / 2, MARGIN), viewport.value.h - cardH - MARGIN)
  const left = Math.min(rect.value.left + rect.value.width + GAP, viewport.value.w - cardW - MARGIN)
  pos.value = { left, top }
  arrowTop.value = Math.min(Math.max(center - top - 8, 16), cardH - 32)
}

function onKey(e) {
  if (!tour.active) return
  if (e.key === 'Escape') {
    e.preventDefault()
    finishTour()
  } else if (e.key === 'ArrowRight') {
    e.preventDefault()
    nextStep()
  } else if (e.key === 'ArrowLeft') {
    e.preventDefault()
    prevStep()
  }
}

watch(
  () => [tour.active, tour.index],
  async () => {
    if (!tour.active) return
    await place()
    await nextTick()
    primaryEl.value?.focus({ preventScroll: true })
  },
  { flush: 'post' },
)

let autoTimer = null
onMounted(() => {
  window.addEventListener('keydown', onKey)
  window.addEventListener('resize', place)
  // Primera vez: se ofrece sola, pero solo cuando ya no hay onboarding pendiente.
  const user = state.user
  if (user?.tour?.pending && !user.onboarding?.pending) {
    autoTimer = setTimeout(() => startTour({ withWelcome: true }), 700)
  }
})
onBeforeUnmount(() => {
  clearTimeout(autoTimer)
  window.removeEventListener('keydown', onKey)
  window.removeEventListener('resize', place)
  if (tour.active) tour.active = false
})
</script>

<style scoped>
.tour { position: fixed; inset: 0; z-index: 4000; }
.tour__shield { position: fixed; inset: 0; }
.tour__dim { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.62); }
.tour__spot {
  position: fixed;
  border-radius: 10px;
  border: 2px solid rgba(96, 165, 250, 0.95);
  box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.62);
  pointer-events: none;
  transition: left 0.25s ease, top 0.25s ease, width 0.25s ease, height 0.25s ease;
}

.tour__card {
  position: fixed;
  width: 340px;
  max-width: calc(100vw - 24px);
  padding: 18px 20px 16px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.35);
  font-family: var(--font-body);
  transition: left 0.25s ease, top 0.25s ease;
}
.tour__card--center { left: 50%; top: 50%; transform: translate(-50%, -50%); width: 400px; text-align: center; padding: 28px 28px 22px; }
.tour__card--bottom { left: 50%; bottom: 16px; transform: translateX(-50%); }

.tour__arrow { position: absolute; left: -7px; width: 14px; height: 14px; background: #fff; transform: rotate(45deg); border-radius: 2px; }

.tour__meta { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.tour__count { font: 500 11px var(--font-mono); color: #94a3b8; letter-spacing: 0.02em; }
.tour__skip { background: none; border: none; padding: 0; font: 400 11px var(--font-body); color: #94a3b8; cursor: pointer; text-decoration: underline; }
.tour__skip:hover { color: #64748b; }

.tour__heading { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.tour__icon { width: 32px; height: 32px; border-radius: 8px; background: rgba(59, 130, 246, 0.12); color: #3b82f6; display: inline-flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.tour__icon--green { width: 56px; height: 56px; border-radius: 50%; background: rgba(16, 185, 129, 0.14); color: #10b981; font-size: 30px; margin: 0 auto 14px; }
.tour__title { margin: 0; font: 700 16px var(--font-display); color: #1e3a5f; }
.tour__card--center .tour__title { font-size: 20px; margin-bottom: 10px; }
.tour__text { margin: 0 0 14px; font: 400 13px/1.6 var(--font-body); color: #475569; }
.tour__card--center .tour__text { margin-bottom: 20px; }

.tour__footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.tour__dots { list-style: none; margin: 0; padding: 0; display: flex; gap: 5px; }
.tour__dot { width: 6px; height: 6px; border-radius: 50%; background: #e2e8f0; }
.tour__dot--done { background: #93c5fd; }
.tour__dot--on { background: #3b82f6; width: 16px; border-radius: 3px; }

.tour__actions { display: flex; gap: 8px; justify-content: flex-end; }
.tour__card--center .tour__actions { justify-content: center; }
.tour__btn { height: 34px; padding: 0 16px; border: none; border-radius: 8px; background: #3b82f6; color: #fff; font: 500 13px var(--font-body); cursor: pointer; white-space: nowrap; }
.tour__btn:hover { background: #2563eb; }
.tour__btn:focus-visible { outline: 2px solid rgba(30, 58, 95, 0.35); outline-offset: 2px; }
.tour__btn--ghost { background: none; color: #64748b; }
.tour__btn--ghost:hover { background: #f1f5f9; }

@media (prefers-reduced-motion: reduce) {
  .tour__spot, .tour__card { transition: none; }
}
</style>